/**
 * BaseAgentNode — Agent 类节点的公共 Canvas 组件
 * 
 * 提取 AgentNode / PersonaAgentNode / StructuredAgentNode / CompanionAgentNode 的通用渲染逻辑。
 * 各节点只需传入颜色主题、图标和副标题即可。
 * 
 * 目前仅 CompanionAgentNode 使用此组件，未来可逐步迁移其他节点。
 */
import { memo } from 'react'
import { Handle, Position } from 'reactflow'

export interface AgentNodeTheme {
  /** 节点背景色 class，如 'bg-sky-50' */
  bg: string
  /** 默认边框色 class，如 'border-sky-200' */
  border: string
  /** 激活时边框色 class，如 'border-sky-500' */
  activeBorder: string
  /** 激活时 ring class，如 'ring-sky-400/50' */
  activeRing: string
  /** 选中时 ring class，如 'ring-sky-500/20' */
  selectedRing: string
  /** 已访问边框色 class，如 'border-sky-300' */
  visitedBorder: string
  /** 已访问 ring class，如 'ring-sky-200/50' */
  visitedRing: string
  /** Handle 颜色（CSS color），如 '#0ea5e9' */
  handleColor: string
  /** 图标容器背景色 class，如 'bg-sky-100' */
  iconBg: string
  /** 图标容器激活背景色 class，如 'bg-sky-500' */
  iconBgActive: string
  /** 标签文字色 class，如 'text-sky-900' */
  labelColor: string
  /** 副标题文字色 class，如 'text-sky-600' */
  subtitleColor: string
}

export interface BaseAgentNodeProps {
  /** ReactFlow node data */
  data: { label: string; [key: string]: unknown }
  /** 是否被用户选中 */
  selected: boolean
  /** 颜色主题 */
  theme: AgentNodeTheme
  /** 图标元素 */
  icon: React.ReactNode
  /** 副标题文字或元素（可选） */
  subtitle?: React.ReactNode
  /** 执行状态（从 ExecutionContext 获取后传入） */
  isActive: boolean
  isVisited: boolean
}

// Handle 基础样式（和 ProjectStudio.tsx 中的 HANDLE_STYLE 一致）
const HANDLE_STYLE_BASE = {
  width: 12,
  height: 12,
  border: '2px solid white',
} as const

/**
 * Agent 类节点通用 Canvas 组件
 */
export const BaseAgentNodeView = memo(({
  data,
  selected,
  theme,
  icon,
  subtitle,
  isActive,
  isVisited,
}: BaseAgentNodeProps) => {
  const borderClass = isActive
    ? `${theme.activeBorder} ring-4 ${theme.activeRing} shadow-xl scale-105 animate-pulse`
    : selected
      ? `${theme.activeBorder} ring-2 ${theme.selectedRing} shadow-xl`
      : isVisited
        ? `${theme.visitedBorder} ring-2 ${theme.visitedRing}`
        : theme.border

  const activeHandleStyle = {
    ...HANDLE_STYLE_BASE,
    background: theme.handleColor,
  }

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg ${theme.bg} border-2 min-w-[180px] transition-all duration-300 ${borderClass}`}>
      <Handle
        type="target"
        position={Position.Left}
        style={selected || isActive ? activeHandleStyle : { ...HANDLE_STYLE_BASE, background: theme.handleColor }}
      />
      
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md transition-colors duration-300 ${isActive ? theme.iconBgActive : theme.iconBg}`}>
          {icon}
        </div>
        <div>
          <div className={`font-medium text-sm ${theme.labelColor}`}>{data.label}</div>
          {subtitle && (
            <div className={`text-xs ${theme.subtitleColor} flex items-center gap-1`}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      
      <Handle
        type="source"
        position={Position.Right}
        style={selected || isActive ? activeHandleStyle : { ...HANDLE_STYLE_BASE, background: theme.handleColor }}
      />
    </div>
  )
})
BaseAgentNodeView.displayName = 'BaseAgentNodeView'
